<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { localizedDynamicTime } from 'shared/helpers/timeHelper';
import { isEmailInbox, isWhatsappInbox } from 'dashboard/constants/gch';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  inboxIds: { type: Array, default: () => [] },
  assigneeIds: { type: Array, default: () => [] },
  lastActivityAt: { type: Number, default: null },
  isExpanded: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'showContact']);

const { t, locale } = useI18n();

const getInbox = useMapGetter('inboxes/getInbox');
const getAgent = useMapGetter('agents/getAgentById');

const channelName = inbox => {
  if (isWhatsappInbox(inbox)) return t('SIDEBAR.WHATSAPP');
  if (isEmailInbox(inbox)) return t('SIDEBAR.EMAIL');
  return inbox.name;
};

// Channels the contact has written through, one entry per channel.
const channels = computed(() => {
  const names = props.inboxIds
    .map(inboxId => getInbox.value(inboxId))
    .filter(inbox => inbox.id)
    .map(channelName);
  return [...new Set(names)].join(', ');
});

const advisors = computed(() =>
  props.assigneeIds
    .map(agentId => getAgent.value(agentId))
    .filter(agent => agent.id)
);

const lastActivity = computed(() =>
  props.lastActivityAt
    ? localizedDynamicTime(props.lastActivityAt, locale.value.replace('_', '-'))
    : ''
);

const details = computed(() => [
  { label: t('CONTACTS_LAYOUT.CARD.DETAILS.EMAIL'), value: props.email },
  { label: t('CONTACTS_LAYOUT.CARD.DETAILS.PHONE'), value: props.phoneNumber },
  { label: t('CONTACTS_LAYOUT.CARD.DETAILS.CHANNEL'), value: channels.value },
]);

const onClickExpand = () => emit('toggle');

const onClickViewDetails = () => emit('showContact', props.id);
</script>

<template>
  <div class="relative">
    <CardLayout :key="id" layout="row">
      <div class="flex items-center justify-start flex-1 gap-4">
        <!-- The avatar opens the contact, like "Ver detalles". -->
        <button
          type="button"
          class="relative flex-shrink-0 rounded-full"
          @click="onClickViewDetails"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="42"
            :status="availabilityStatus"
            hide-offline-status
          />
        </button>
        <div class="flex flex-col gap-0.5 flex-1">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ name }}
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <div v-if="email" class="truncate max-w-72" :title="email">
              <span class="text-sm text-n-slate-11">
                {{ email }}
              </span>
            </div>
            <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
            <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
              {{ phoneNumber }}
            </span>
            <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              @click="onClickViewDetails"
            />
          </div>
        </div>
      </div>

      <Button
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <dl
              class="grid grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))] gap-x-6 gap-y-4 m-0 px-6 py-5 border-t border-n-strong"
            >
              <div
                v-for="detail in details"
                :key="detail.label"
                class="flex flex-col gap-1 min-w-0"
              >
                <dt class="text-xs text-n-slate-10">{{ detail.label }}</dt>
                <dd class="m-0 text-sm truncate text-n-slate-12">
                  {{ detail.value || t('CONTACTS_LAYOUT.CARD.DETAILS.EMPTY') }}
                </dd>
              </div>
              <div class="flex flex-col gap-1 min-w-0">
                <dt class="text-xs text-n-slate-10">
                  {{ t('CONTACTS_LAYOUT.CARD.DETAILS.ADVISOR') }}
                </dt>
                <dd class="flex items-center gap-1.5 m-0">
                  <Avatar
                    v-for="advisor in advisors"
                    :key="advisor.id"
                    v-tooltip.top="advisor.name"
                    :name="advisor.name"
                    :src="advisor.thumbnail"
                    :size="20"
                    rounded-full
                  />
                  <Icon
                    v-if="!advisors.length"
                    v-tooltip.top="t('CONTACTS_LAYOUT.CARD.DETAILS.UNASSIGNED')"
                    icon="i-woot-empty-assignee"
                    class="size-5 text-n-slate-7"
                  />
                </dd>
              </div>
              <div class="flex flex-col gap-1 min-w-0">
                <dt class="text-xs text-n-slate-10">
                  {{ t('CONTACTS_LAYOUT.CARD.DETAILS.LAST_ACTIVITY') }}
                </dt>
                <dd
                  class="m-0 text-sm truncate text-n-slate-12 first-letter:uppercase"
                >
                  {{ lastActivity || t('CONTACTS_LAYOUT.CARD.DETAILS.EMPTY') }}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
